import React, { memo, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, type TextStyle, type LayoutChangeEvent } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSpring } from 'react-native-reanimated';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { androidScaleFont, androidScaleSize } from '../../shared/theme/studio/stackScreenShell';
import { JOURNEY_SPRING } from '../../shared/theme/studio/journeyMotion';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

type Props = {
  colors: ThemeColors;
  step: number;
  total_steps: number;
  step_label: string;
};

export const JourneyProgressBar = memo(function JourneyProgressBar({
  colors,
  step,
  total_steps,
  step_label,
}: Props) {
  const rtl = useRtl();
  const sf = androidScaleFont;
  const styles = useMemo(() => createStyles(colors, sf), [colors]);
  const track_width = useSharedValue(0);
  const progress = useSharedValue((step + 1) / total_steps);
  const fill_min_width = androidScaleSize(12);
  const glow_inset = androidScaleSize(6);

  useEffect(() => {
    progress.value = withSpring((step + 1) / total_steps, JOURNEY_SPRING);
  }, [step, total_steps, progress]);

  const on_track_layout = (event: LayoutChangeEvent) => {
    track_width.value = event.nativeEvent.layout.width;
  };

  const fill_style = useAnimatedStyle(() => ({
    width: Math.max(track_width.value * progress.value, fill_min_width),
  }));

  const glow_style = useAnimatedStyle(() => ({
    transform: [{ translateX: track_width.value * progress.value - glow_inset }],
    opacity: progress.value > 0.08 ? 1 : 0,
  }));

  const step_num = String(step + 1).padStart(2, '0');
  const total_num = String(total_steps).padStart(2, '0');

  return (
    <View style={styles.wrap}>
      <View style={styles.meta_row}>
        <View style={styles.step_badge}>
          <Text style={styles.step_badge_text}>
            {step_num}
            <Text style={styles.step_badge_muted}> / {total_num}</Text>
          </Text>
        </View>
        <Text style={[styles.caption, { textAlign: rtl.textAlign, writingDirection: rtl.writingDirection }]} numberOfLines={1}>
          {step_label}
        </Text>
      </View>

      <View style={[styles.track_shell, horizontalLtrDirectionStyle]} onLayout={on_track_layout}>
        <View style={styles.track_bg} />
        <Animated.View style={[styles.track_fill, { backgroundColor: colors.primary }, fill_style]} />
        <Animated.View style={[styles.track_glow, { backgroundColor: colors.primary }, glow_style]} />
      </View>
    </View>
  );
});

function createStyles(c: ThemeColors, sf: (n: number) => number) {
  return StyleSheet.create({
    wrap: {
      marginBottom: 0,
      paddingTop: appTheme.spacing.xs,
    },
    meta_row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.md,
      marginBottom: appTheme.spacing.sm,
    },
    step_badge: {
      paddingHorizontal: androidScaleSize(10),
      paddingVertical: androidScaleSize(5),
      borderRadius: androidScaleSize(999),
      backgroundColor: hexToRgba(c.primary, 0.1),
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.primary, 0.22),
    },
    step_badge_text: {
      fontSize: sf(12),
      fontWeight: '800',
      letterSpacing: 0.6,
      color: c.primary,
      ...ANDROID_TEXT_COMPACT,
    },
    step_badge_muted: {
      fontWeight: '700',
      color: hexToRgba(c.primary, 0.55),
    },
    caption: {
      flex: 1,
      fontSize: sf(12),
      fontWeight: '700',
      letterSpacing: 0.35,
      color: c.textSecondary,
      textTransform: 'uppercase',
      ...ANDROID_TEXT_COMPACT,
    },
    track_shell: {
      height: androidScaleSize(6),
      justifyContent: 'center',
    },
    track_bg: {
      ...StyleSheet.absoluteFillObject,
      borderRadius: androidScaleSize(999),
      backgroundColor: hexToRgba(c.text, 0.08),
    },
    track_fill: {
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      borderRadius: androidScaleSize(999),
    },
    track_glow: {
      position: 'absolute',
      top: androidScaleSize(-3),
      width: androidScaleSize(12),
      height: androidScaleSize(12),
      borderRadius: androidScaleSize(6),
      opacity: 0.35,
    },
  });
}
